import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {onAuthStateChanged} from 'firebase/auth'
import {auth} from '../../firebase'
import {
  AppThemeDoc,
  ActiveThemeSettingsDoc,
  subscribeToActiveThemeSettings,
  subscribeToThemes,
  ThemeMode,
  ThemeOverrides,
} from '../services/themeCustomizerService'
import {BUILT_IN_THEMES, findBuiltInTheme, isBuiltInThemeId} from '../themes/builtinThemes'

type ThemeWithId = {id: string; slug?: string; isBuiltIn?: boolean} & AppThemeDoc

type ThemeCustomizerContextType = {
  themes: ThemeWithId[]
  activeThemeId: string | null
  activeTheme: ThemeWithId | null
  isLoaded: boolean
  applyOverridesForCurrentMode: (overrides: ThemeOverrides) => void
  clearOverrides: () => void
}

const ThemeCustomizerContext = createContext<ThemeCustomizerContextType>({
  themes: [],
  activeThemeId: null,
  activeTheme: null,
  isLoaded: false,
  applyOverridesForCurrentMode: () => {},
  clearOverrides: () => {},
})

function getCurrentBsThemeMode(): ThemeMode {
  const mode = document.documentElement.getAttribute('data-bs-theme')
  return mode === 'dark' ? 'dark' : 'light'
}

function applyOverrides(overrides: ThemeOverrides) {
  const root = document.documentElement
  Object.entries(overrides).forEach(([key, value]) => {
    if (!key.startsWith('--')) return
    if (value === '' || value == null) {
      root.style.removeProperty(key)
    } else {
      root.style.setProperty(key, value)
    }
  })
}

function clearAllAppliedOverrides(keys: string[]) {
  const root = document.documentElement
  keys.forEach((k) => {
    if (k.startsWith('--')) root.style.removeProperty(k)
  })
}

// Tint the PWA/browser window title bar to match the active theme. The title bar
// follows <meta name="theme-color">, which we point at the resolved app background
// so it changes with the theme (warm under graphite, dark under terminal, etc.)
// instead of the hardcoded black. Read computed so it works for built-in and
// custom themes, light and dark. Call AFTER overrides are (re)applied/cleared.
function syncTitleBarColor() {
  // index.html now ships TWO media-gated theme-color metas (light/dark) for a
  // correct first paint. Once the app theme resolves, the ACTIVE theme wins on
  // both — update every meta, not just the first (a dark-OS browser reads the
  // second one, which querySelector would never touch).
  const metas = document.querySelectorAll('meta[name="theme-color"]')
  if (metas.length === 0) return
  const cs = getComputedStyle(document.documentElement)
  const color = (cs.getPropertyValue('--bs-body-bg') || cs.getPropertyValue('--kt-app-bg-color')).trim()
  if (color) metas.forEach((m) => m.setAttribute('content', color))
}

export const ThemeCustomizerProvider = ({children}: {children: React.ReactNode}) => {
  // Firestore-backed themes (user-created via the Theme Customizer UI)
  const [firestoreThemes, setFirestoreThemes] = useState<ThemeWithId[]>([])
  const [activeSettings, setActiveSettings] = useState<ActiveThemeSettingsDoc | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const appliedKeysRef = useRef<string[]>([])
  const appliedSlugRef = useRef<string | null>(null)

  // Merge built-in themes (defined in code) with Firestore themes.
  // Built-ins come first so they appear at the top of the switcher.
  const themes = useMemo<ThemeWithId[]>(() => {
    return [...BUILT_IN_THEMES, ...firestoreThemes]
  }, [firestoreThemes])

  const activeThemeId = activeSettings?.activeThemeId ?? null
  const activeTheme = useMemo<ThemeWithId | null>(() => {
    // Built-in themes resolve synchronously from code without a Firestore fetch.
    if (isBuiltInThemeId(activeThemeId)) {
      const builtin = findBuiltInTheme(activeThemeId)
      return builtin ?? null
    }
    return firestoreThemes.find((t) => t.id === activeThemeId) ?? null
  }, [firestoreThemes, activeThemeId])

  // Subscribe only once the user is authenticated (Firestore rules require auth)
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setFirestoreThemes([])
        setActiveSettings(null)
        setIsLoaded(true)
        return
      }

      const unsubThemes = subscribeToThemes((items) => setFirestoreThemes(items))
      // Per-user theme preference: subscribes to users/{uid} so each
      // user's choice is isolated. Unsubscribes + resubscribes cleanly
      // on account switch because this whole effect block is inside
      // onAuthStateChanged.
      const unsubActive = subscribeToActiveThemeSettings(user.uid, (settings) => {
        setActiveSettings(settings)
        setIsLoaded(true)
      })

      // Clean up when auth changes
      return () => {
        unsubThemes()
        unsubActive()
      }
    })

    return () => unsubAuth()
  }, [])

  // Apply active theme overrides whenever active theme changes or mode changes
  useEffect(() => {
    // Remove previously applied keys first
    clearAllAppliedOverrides(appliedKeysRef.current)
    appliedKeysRef.current = []

    // Sync the `data-app-theme` attribute: companion SCSS (e.g. terminal.scss)
    // targets this to handle things CSS variables alone can't (font family,
    // scrollbar style, ligature suppression, section dividers).
    const root = document.documentElement
    if (appliedSlugRef.current && appliedSlugRef.current !== activeTheme?.slug) {
      root.removeAttribute('data-app-theme')
      appliedSlugRef.current = null
    }
    if (activeTheme?.slug) {
      // Variants (e.g. Blossom) reuse another theme's scoped SCSS via appThemeAttr.
      root.setAttribute('data-app-theme', (activeTheme as any).appThemeAttr || activeTheme.slug)
      appliedSlugRef.current = activeTheme.slug
    }

    if (!activeTheme) { syncTitleBarColor(); return }
    const mode: ThemeMode = getCurrentBsThemeMode()
    const overrides = activeTheme.overrides?.[mode] ?? {}

    appliedKeysRef.current = Object.keys(overrides)
    applyOverrides(overrides)
    syncTitleBarColor()

    // Re-apply on mode changes
    const observer = new MutationObserver(() => {
      clearAllAppliedOverrides(appliedKeysRef.current)
      appliedKeysRef.current = []
      const nextMode: ThemeMode = getCurrentBsThemeMode()
      const nextOverrides = activeTheme.overrides?.[nextMode] ?? {}
      appliedKeysRef.current = Object.keys(nextOverrides)
      applyOverrides(nextOverrides)
      syncTitleBarColor()
    })
    observer.observe(document.documentElement, {attributes: true, attributeFilter: ['data-bs-theme']})
    return () => observer.disconnect()
  }, [activeThemeId, activeTheme])

  const ctxValue = useMemo<ThemeCustomizerContextType>(() => {
    return {
      themes,
      activeThemeId,
      activeTheme,
      isLoaded,
      applyOverridesForCurrentMode: (overrides: ThemeOverrides) => {
        const mode = getCurrentBsThemeMode()
        // Track keys so we can clear them later
        appliedKeysRef.current = Object.keys(overrides)
        clearAllAppliedOverrides(appliedKeysRef.current)
        applyOverrides(overrides)
      },
      clearOverrides: () => {
        clearAllAppliedOverrides(appliedKeysRef.current)
        appliedKeysRef.current = []
      },
    }
  }, [themes, activeThemeId, activeTheme, isLoaded])

  return <ThemeCustomizerContext.Provider value={ctxValue}>{children}</ThemeCustomizerContext.Provider>
}

export const useThemeCustomizer = () => useContext(ThemeCustomizerContext)



















